import { useRouteLoaderData, useSearchParams } from 'react-router';
import type { LoaderFunctionArgs } from '~/types/route-args';
import VideosPage from '@modules/professional/page/VideosPage';
import { loader as videosLoader } from '@modules/professional/page/loaders/videos-page-loader.server';
import type { loader as layoutLoader } from '@modules/professional/page/loaders/professional-page-loader.server';
import { ProfessionalVideosPageRevamp } from '~/ui-rocco/pages/professional-detail/ProfessionalVideosPageRevamp';
import { shouldRevalidateLeaf } from '~/modules/root/util/should-revalidate-leaf';
import type { APHandle } from '~/modules/root';
import { mergeMeta } from '~/modules/seo';
import { createVideoSchema } from '~/modules/seo/schema/video-schema';
import { createLDSchema } from '~/modules/seo/util/create-ld-schema';
import dayjs from 'dayjs';

export const meta = mergeMeta<typeof loader>(
    (_props) => {
        return [];
    },

    (props) => {
        const videoSchemas =
            props.data?.companyVideosResult?.videos.map((video) => {
                return createVideoSchema({
                    name: video?.caption ?? '',
                    publishDate: dayjs(video?.publishDate).isValid()
                        ? dayjs(video?.publishDate).toISOString()
                        : ' ',
                    professionalName: video.caption ?? '',
                    url: video?.src ?? '',
                    thumbnailUrl: video?.coverImage.src ?? '',
                    description: video.caption ?? '',
                });
            }) ?? [];

        const ldSchema = createLDSchema(videoSchemas);

        return [...ldSchema];
    },
);

/**
 * When the revamp is active the Videos tab becomes a full editorial page that
 * reuses the overview sections (hero, "Talk to us", closing) + a click-to-play
 * video grid, so the loader merges overview-shaped data with the videos result.
 * Otherwise it serves the legacy paginated video grid.
 */
export const loader = async (args: LoaderFunctionArgs) => {
    // The revamp's shared overview payload comes from the persistent layout
    // loader; both variants only need the videos result here.
    return videosLoader(args);
};

export const handle: APHandle = {
    professionalRevampLayout: true,
};

const ProfessionalVideosRoute = () => {
    const layoutData = useRouteLoaderData<typeof layoutLoader>(
        'routes/_app.professional.$urlSegment',
    );
    const [searchParams] = useSearchParams();
    const showRevamp =
        layoutData?.detailsRevamp ||
        (import.meta.env.DEV &&
            (searchParams.get('revamp') === '1' ||
                searchParams.get('detailsRevamp') === '1'));

    return showRevamp ? <ProfessionalVideosPageRevamp /> : <VideosPage />;
};

export const shouldRevalidate = shouldRevalidateLeaf;
export default ProfessionalVideosRoute;
